/*
 * Copyright © 2018-2023, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import { notificationType, strings } from "../../../common/Strings/Strings";
import AlertBox from "../../../components/AlertBox";
import { getGeneralError } from "../../../util/helperFunctions";
import Modal from "../../../components/Modal/Modal";
import LoaderInline from "../../../components/LoaderInline";
import { NotificationType } from "../../../models/notification/NotificationType";
import { NotificationPreviewResponse } from "../../../models/notification/NotificationPreviewResponse";
import TestNotificationApi from "../../../api/TestNotificationApi";
import EmailNotificationPreview from "./EmailNotificationPreview";
import CloseButton from "../../../components/CloseButton";
import PushNotificationPreview from "./PushNotificationPreview";
import Tabs from "../../../components/Tabs/Tabs";
import InAppNotificationPreview from "./InAppNotificationPreview";
import SmsNotificationPreview from "./SmsNotificationPreview";
import Button from "../../../components/Button";

interface Props {
  close: () => void;
  isVisible: boolean;
  type: NotificationType;
}

const NotificationPreviewModal: React.FC<Props> = (props: Props) => {
  const { close, isVisible, type } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [testEmailLoading, setTestEmailLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<NotificationPreviewResponse>();
  const [key, setKey] = useState<string>();

  useEffect(() => {
    const getPreview = async (type: NotificationType) => {
      try {
        setLoading(true);
        setError(null);
        setPreview(undefined);

        const response = await TestNotificationApi.getPreview(type);
        setPreview(response.data);
        setKey(response.data.email ? "email" : "notification");
      } catch (e) {
        setError(await getGeneralError(e));
      } finally {
        setLoading(false);
      }
    };

    void getPreview(type);
  }, [type]);

  const sendTestEmail = async (type: NotificationType) => {
    try {
      setTestEmailLoading(true);
      setError(null);
      await TestNotificationApi.sendTestEmail(type);
    } catch (e) {
      setError(await getGeneralError(e));
    } finally {
      setTestEmailLoading(false);
    }
  };

  const content = () => {
    switch (key) {
      case "email": {
        return (
          preview?.email && (
            <EmailNotificationPreview
              title={preview.email.title}
              message={preview.email.message}
              path={preview.email.path || "/"}
              actionButton={preview.email.actionButton}
            />
          )
        );
      }
      case "notification": {
        return (
          preview?.notification && (
            <div className="space-y-4">
              <InAppNotificationPreview title={preview.notification.title} message={preview.notification.message} />
              <PushNotificationPreview title={preview.notification.title} message={preview.notification.message} />
              <SmsNotificationPreview title={preview.notification.title} message={preview.notification.message} />
            </div>
          )
        );
      }
      default:
        return <></>;
    }
  };

  const tabs = () => {
    const tabs = [];
    if (preview?.email) {
      tabs.push({
        title: strings.email,
        tab: "email",
      });
    }
    if (preview?.notification) {
      tabs.push({
        title: strings.notification,
        tab: "notification",
      });
    }
    return tabs;
  };

  return (
    <>
      <Modal size="modal-lg" show={isVisible} handleClose={close}>
        <Modal.Header closeButton title={notificationType[type]}></Modal.Header>
        <Modal.Body>
          <div hidden={!loading}>
            <LoaderInline />
          </div>
          <div hidden={!preview}>
            <div className="space-y-4">
              <Tabs tabs={tabs()} currentTabKey={key} setTab={setKey} />
              <div>{content()}</div>
            </div>
          </div>
          <AlertBox message={error} className="my-3" />
        </Modal.Body>
        <Modal.Footer>
          <>
            {preview?.email && (
              <Button
                className="modal-main-button"
                disabled={testEmailLoading}
                loading={testEmailLoading}
                onClick={() => sendTestEmail(type)}
              >
                {strings.sendTestEmail}
              </Button>
            )}
            <CloseButton onClick={close} />
          </>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default NotificationPreviewModal;
